import { setCookie, getCookie } from "@/utils.js";

/**
 * Recent tasks management composable
 * Handles cookie operations for tracking recently opened tasks
 */
export function useRecentTasks() {
   const COOKIE_NAME = "recentTasks";
   const LAST_TASK_COOKIE_NAME = "lastOpenedTask";
   const MAX_RECENT_TASKS = 5;
   const COOKIE_EXPIRY_DAYS = 30;

   /**
    * Adds a task to recent tasks cookie
    * @param {Object} taskData - Task data object
    * @param {Array} notes - Optional notes array for last message
    * @param {string} currentPath - Current router path
    */
   const addToRecentTasks = (taskData, notes = [], currentPath = "") => {
      try {
         // Get existing recent tasks
         let recentTasks = [];
         const existingCookie = getCookie(COOKIE_NAME);
         if (existingCookie) {
            recentTasks = JSON.parse(existingCookie);
         }

         // Get last message from notes
         let lastMessage = null;
         if (notes && notes.length > 0) {
            const lastNote = notes[notes.length - 1];
            lastMessage = {
               text: lastNote.note || "",
               author: lastNote.author || "",
               timestamp: lastNote.insertedDatetime || "",
            };
         }

         // Create new task object with full details
         const newTask = {
            id: taskData.id,
            taskId: taskData.id,
            orderId: taskData.orderId || 0,
            path: currentPath,
            timestamp: new Date().toISOString(),
            status: taskData.status,
            subStatus: taskData.subStatus,
            groupPnrDetails: {
               groupName: taskData.groupPnrDetails?.groupName,
               departureAirport: taskData.groupPnrDetails?.departureAirport,
               arrivalAirport: taskData.groupPnrDetails?.arrivalAirport,
               departureDate: taskData.groupPnrDetails?.departureDate,
               returnDate: taskData.groupPnrDetails?.returnDate,
               cabinType: taskData.groupPnrDetails?.cabinType,
               paxCounts: taskData.groupPnrDetails?.paxCounts,
            },
            openedDatetime: taskData.openedDatetime,
            lastMessage: lastMessage,
         };

         // Remove if already exists (to move it to top)
         recentTasks = recentTasks.filter((t) => t.taskId != taskData.id);

         // Add to beginning
         recentTasks.unshift(newTask);

         // Keep only last N tasks
         recentTasks = recentTasks.slice(0, MAX_RECENT_TASKS);

         // Save to cookie
         setCookie(COOKIE_NAME, JSON.stringify(recentTasks), COOKIE_EXPIRY_DAYS, 0);

         // Also save single lastOpenedTask for backward compatibility
         const simpleTask = {
            taskId: taskData.id,
            orderId: taskData.orderId || 0,
            path: currentPath,
            timestamp: new Date().toISOString(),
         };
         setCookie(LAST_TASK_COOKIE_NAME, JSON.stringify(simpleTask), COOKIE_EXPIRY_DAYS, 0);
      } catch (error) {
         console.error("Error saving recent tasks:", error);
      }
   };

   /**
    * Gets all recent tasks from cookie
    * @returns {Array} Array of recent task objects
    */
   const getRecentTasks = () => {
      try {
         const recentTasksCookie = getCookie(COOKIE_NAME);
         if (recentTasksCookie) {
            const tasks = JSON.parse(recentTasksCookie);
            return Array.isArray(tasks) ? tasks : [];
         }
      } catch (error) {
         console.error("Error loading recent tasks:", error);
      }
      return [];
   };

   /**
    * Gets the last opened task from cookie
    * @returns {Object|null} Last task object or null
    */
   const getLastTask = () => {
      try {
         const lastTaskCookie = getCookie(LAST_TASK_COOKIE_NAME);
         if (lastTaskCookie) {
            return JSON.parse(lastTaskCookie);
         }
      } catch (error) {
         console.error("Error loading last task:", error);
      }
      return null;
   };

   /**
    * Checks if there's a last opened task
    * @returns {boolean} True if last task exists
    */
   const hasLastTask = () => {
      try {
         const lastTask = getCookie(LAST_TASK_COOKIE_NAME);
         return !!lastTask;
      } catch (error) {
         return false;
      }
   };

   /**
    * Clears all recent tasks from cookie
    */
   const clearRecentTasks = () => {
      try {
         setCookie(COOKIE_NAME, "", -1, 0);
         setCookie(LAST_TASK_COOKIE_NAME, "", -1, 0);
      } catch (error) {
         console.error("Error clearing recent tasks:", error);
      }
   };

   return {
      addToRecentTasks,
      getRecentTasks,
      getLastTask,
      hasLastTask,
      clearRecentTasks,
   };
}
